class KlarnaMonthlyCalculator extends HTMLElement {
  constructor() {
    super();
    this.variantChangeUnsubscriber = undefined;
    this.onProductSwap = this.onProductSwap.bind(this);
    this.onProductInfoChange = this.onProductInfoChange.bind(this);
  }

  connectedCallback() {
    this.productInfo = this.closest(".product__info-container");
    this.subscribeToVariantChanges();
    this.bindDomListeners();
    this.recalculateFromDomWithRetry();
  }

  disconnectedCallback() {
    if (this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber();
    }

    this.unbindDomListeners();
  }

  bindDomListeners() {
    document.addEventListener("productLowerUpdated", this.onProductSwap);
    document.addEventListener("colorVariantChanged", this.onProductSwap);

    if (this.productInfo) {
      this.productInfo.addEventListener("change", this.onProductInfoChange);
    }
  }

  unbindDomListeners() {
    document.removeEventListener("productLowerUpdated", this.onProductSwap);
    document.removeEventListener("colorVariantChanged", this.onProductSwap);

    if (this.productInfo) {
      this.productInfo.removeEventListener("change", this.onProductInfoChange);
    }
  }

  subscribeToVariantChanges() {
    this.variantChangeUnsubscriber = subscribe(
      PUB_SUB_EVENTS.variantChange,
      (event) => {
        if (event.data.sectionId === this.dataset.sectionId) {
          this.updateCalculation(event.data.variant?.price || 0);
        }
      },
    );
  }

  onProductSwap() {
    this.recalculateFromDomWithRetry();
  }

  onProductInfoChange(event) {
    if (!event?.target?.classList?.contains("product-variant-id")) return;
    this.recalculateFromDomWithRetry();
  }

  recalculateFromDomWithRetry() {
    let attempts = 0;

    const run = () => {
      attempts += 1;

      const pricePence = this.getCurrentPricePenceFromDom();
      if (pricePence > 0) {
        this.updateCalculation(pricePence);
        return;
      }

      if (attempts < 6) {
        setTimeout(run, 80);
      }
    };

    run();
  }

  getCurrentVariantFromDom() {
    const productInfo =
      this.productInfo || this.closest(".product__info-container");
    if (!productInfo) return null;

    const variantIdInput = productInfo.querySelector(".product-variant-id");
    const variantId = parseInt(
      variantIdInput?.value || this.dataset.productVariantId || "",
      10,
    );
    if (!variantId) return null;

    const variantDataScript = productInfo.querySelector(
      "variant-radios script[type='application/json']",
    );
    if (!variantDataScript?.textContent) return null;

    try {
      const variants = JSON.parse(variantDataScript.textContent);
      return variants.find((variant) => variant.id === variantId) || null;
    } catch (error) {
      return null;
    }
  }

  getCurrentPricePenceFromDom() {
    const variant = this.getCurrentVariantFromDom();
    if (variant?.price) return parseInt(variant.price, 10);

    const productInfo =
      this.productInfo || this.closest(".product__info-container");
    if (!productInfo) return 0;

    const wishlistPrice = productInfo.querySelector(
      ".product__wishlist[data-price]",
    )?.dataset?.price;
    if (wishlistPrice) {
      const wishlistValue = parseFloat(wishlistPrice);
      if (!Number.isNaN(wishlistValue)) {
        return Math.round(wishlistValue * 100);
      }
    }

    return 0;
  }

  updateCalculation(pricePence = 0) {
    const messageEls = this.querySelectorAll(
      ".js-klarna-monthly-calculator__message",
    );
    if (!messageEls.length || !pricePence) return;

    const price = parseInt(pricePence, 10);
    if (!price) return;

    const multiplier = parseFloat(this.dataset.multiplier);
    const minThreshold = parseInt(this.dataset.minPriceThreshold, 10);
    const messageTemplate =
      this.dataset.messageTemplate ||
      "Available from {monthly_price} per month.";

    messageEls.forEach((messageEl) => {
      if (price < minThreshold) {
        messageEl.classList.add("hidden");
        return;
      }

      messageEl.classList.remove("hidden");

      const monthlyPricePence = Math.ceil(price * multiplier);
      const formattedPrice = Shopify.formatMoney(
        monthlyPricePence,
        this.dataset.moneyFormat || "£{{ amount }}",
      );
      const message = messageTemplate.replace(
        "{monthly_price}",
        formattedPrice,
      );

      messageEl.innerHTML = message;
    });
  }
}

if (!customElements.get("klarna-monthly-calculator")) {
  customElements.define("klarna-monthly-calculator", KlarnaMonthlyCalculator);
}